import { FiltrosPrestacaoContasMensal } from "@/types/entregas/pcm/filtros-prestacao-contas-mensal";
import { PrestacaoDeContasMensal } from "@/types/entregas/pcm/prestacao-contas-mensal";
import { Service } from "../mainService";
import { RelatorioPCM } from "@/types/entregas/pcm/relatorio-pcm";
import { PortalError } from "@/types/portal-error";
import { LogVisualizacaoRelatorio } from "@/types/relatorios/log-visualizacao-relatorio";
import { Relatorio } from "@/types/relatorios/relatorio";
import { ContaCaixa } from "@/types/entregas/pcm/conta-caixa";
import { ContaCaixaResumida } from "@/types/entregas/pcm/conta-caixa-resumida";
import { SituacaoPcm } from "@/types/entregas/pcm/situacao-pcm";
import { RetornoFilaPCM } from "@/types/entregas/pcm/retorno-fila-pcm";
import { ContasInativar } from "@/types/entregas/pcm/conta-inativar";

const pcmService = {
  getCombosConsultaPCM,
  getExercicios,
  getTipoPrestacaoUG,
  getTipoEntrega,
  getMeses,
  consultarPrestacoes,
  salvarPrestacao,
  getByID,
  downloadEntrega,
  downloadRecibo,
  consultarRelatorios,
  downloadRelatorio,
  downloadRelatorioParametrosAdicionais,
  registrosAssinatura,
  assinaturaSenha,
  iniciarAssinaturaToken,
  finalizarAssinaturaToken,
  confirmarVisualizacaoRelatorioObrigatorio,
  validarVisualizacaoRelObrigatorios,
  cancelarPcm,
  simularAssinatura,
  liberarReenvio,
  listarContasCaixa,
  atualizarContaCaixa,
  carregarFilaPcm,
  getContasInativar,
  patchContasInativar,
};

/**
 * @returns os combos que são utilizados na página de consulta de entregas de PCM.
 * @since 20220927
 */
async function getCombosConsultaPCM() {
  try {
    const tiposPrestacao = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/tipoPrestacaoUG",
    );
    const tiposEntrega = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/tiposEntregaConsulta",
    );
    const situacoes = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/situacoespcm",
    );
    return {
      tiposPrestacao: tiposPrestacao.data,
      tiposEntrega: tiposEntrega.data,
      situacoes: situacoes.data.map((s: any) => new SituacaoPcm(s)),
    };
  } catch (e: any) {
    return e.response;
  }
}

async function salvarPrestacao(data: FormData) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/salvarPCM/",
      data,
      { responseType: "blob" },
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function consultarRelatorios(idpcm: string): Promise<RelatorioPCM[]> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/pcm/relatoriosPCM/" + idpcm,
  );
  // console.log("pcmservice.consultarRelatiors recebeu", response);
  if (response && response.status === 200)
    return response.data.map((r) => {
      const rel = Object.assign(new RelatorioPCM(), r);
      if (rel.IdRelatorioNavigation) {
        rel.IdRelatorioNavigation = Object.assign(
          new Relatorio(),
          r.IdRelatorioNavigation,
        );
        if (
          rel.IdRelatorioNavigation.LogVisualizacao &&
          rel.IdRelatorioNavigation.LogVisualizacao.length
        )
          rel.IdRelatorioNavigation.LogVisualizacao =
            rel.IdRelatorioNavigation.LogVisualizacao.map((lv) => {
              const lvv = Object.assign(new LogVisualizacaoRelatorio(), lv);
              lvv.tratarDadosDaAPI();
              return lvv;
            });
      }
      return rel;
    });
  throw new PortalError("Erro ao obter lista de relatórios da prestação.");
}

async function registrosAssinatura(idpcm: number) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/registrosAssinatura/" + idpcm,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function assinaturaSenha(data: any) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/assinarPCM",
      data,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function iniciarAssinaturaToken(data: any) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/iniciarAssinaturaToken",
      data,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function finalizarAssinaturaToken(data: any) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/finalizarAssinaturaToken",
      data,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function confirmarVisualizacaoRelatorioObrigatorio(
  idLog: number,
): Promise<LogVisualizacaoRelatorio> {
  const response = await Service.$http.patch(
    process.env.VUE_APP_REPORTS_API +
    "/relatoriopcm/confirmarVisualizacao/" +
    idLog,
  );
  if (response && response.status === 200) {
    const logrel = Object.assign(new LogVisualizacaoRelatorio(), response.data);
    logrel.tratarDadosDaAPI();
    return logrel;
  }
  throw new PortalError(
    response.data.msg ?? "Erro ao confirmar visualização de relatório",
  );
}

/**
 * Verifica se todos os relatórios obrigatórios de uma PCM já foram visualizados.
 * Sem isso não é possível assinar uma PCM.
 *
 * @param idPcm
 * @returns
 * @since 20230821
 */
async function validarVisualizacaoRelObrigatorios(
  idPcm: number,
): Promise<boolean> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API +
    "/pcm/validarVisualizacaoRelObrigatorios/" +
    idPcm,
  );
  if (response && response.status === 200) {
    return response.data;
  }
  throw new PortalError(
    response.data.msg ??
    "Erro ao validar se os relatórios obrigatórios foram lidos!",
  );
}

async function downloadRelatorio(idRelPCM: string, idPCM: string) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_REPORTS_API +
      `/relatoriopcm/relatorioPcm?idPCM=${idPCM}&idRelPcm=${idRelPCM}`,
      //{ responseType: "blob" }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function downloadRelatorioParametrosAdicionais(
  idRelPCM: string,
  idpcm: string,
  paramsAdicionais: any,
) {
  try {
    let url =
      process.env.VUE_APP_REPORTS_API +
      `/relatoriopcm/relatorioPcm?idPCM=${idpcm}&idRelPcm=${idRelPCM}`;
    if (
      paramsAdicionais.codUnidadeOrcamentaria !== null &&
      paramsAdicionais.codUnidadeOrcamentaria !== ""
    ) {
      url += "&codigoUO=" + paramsAdicionais.codUnidadeOrcamentaria;
    }
    if (
      paramsAdicionais.numeroEmpenho !== null &&
      paramsAdicionais.numeroEmpenho !== ""
    ) {
      url += "&nrEmpenho=" + paramsAdicionais.numeroEmpenho;
    }
    if (
      paramsAdicionais.tipoRestoAPagar !== null &&
      paramsAdicionais.tipoRestoAPagar !== ""
    ) {
      url += "&tipoRP=" + paramsAdicionais.tipoRestoAPagar;
    }
    if (
      paramsAdicionais.anoEmpenho !== null &&
      paramsAdicionais.anoEmpenho !== ""
    ) {
      url += "&ano=" + paramsAdicionais.anoEmpenho;
    }
    const response = await Service.$http.get(url);
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getExercicios() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/Exercicios",
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getTipoPrestacaoUG() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/tipoPrestacaoUG",
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getTipoEntrega(tipoPrestacao: string, ano: string, mes: string) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API +
      "/pcm/tiposEntrega/" +
      tipoPrestacao +
      "/" +
      ano +
      "/" +
      mes,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getMeses(tipoPrestacaoUG: string) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/meses/" + tipoPrestacaoUG,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

/**
 * Se houver múltiplos filtros por situação, tipo '1,2,3,4' ao invés de '1',
 * este método gera uma query string no formato &situacao=1&situacao=2&situacao=3&situacao=4.
 * TODO: Deve ser mais limpo gerar um toQueryString sem situações, e montar a string de situações por último. A chamada que usa isso aqui sempre começará com os parâmetros de paginação, então a lista de situações é mais simples de montar.
 */
function gerarFiltrosConsultaPcm(
  filtros: FiltrosPrestacaoContasMensal,
): string {
  let queryString = filtros.toQueryString();
  const situacao = filtros.situacao;
  if (situacao && typeof situacao === "string" && situacao.includes(",")) {
    // Remove any existing situacao from queryString
    queryString = queryString.replace(/&?situacao=[^&]*/g, "");
    const situacoes = situacao
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
    const situacaoParams = situacoes
      .map((s: string) => `&situacao=${encodeURIComponent(s)}`)
      .join("");
    queryString += situacaoParams;
  }
  return queryString;
}

async function consultarPrestacoes(
  filtros: FiltrosPrestacaoContasMensal,
  page: number,
): Promise<any> {
  try {
    const queryString = gerarFiltrosConsultaPcm(filtros);

    const parametros = `?page=${page}&pageSize=10${queryString}`;
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API +
      "/pcm/consultarPrestacoes" +
      parametros,
    );
    //TODO: criar objeto de retorno padrão, com array de PrestacaoDeContasMensal já tratado.
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getByID(idpcm: number): Promise<PrestacaoDeContasMensal> {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/consultarPrestacaoById/" + idpcm,
    );
    //console.log('sou o pcmService.getByID. Response ', response)
    return {
      ...response.data,
      DataEntrega: new Date(response.data.DataEntrega),
    };
  } catch (e: any) {
    return e.response;
  }
}

async function downloadEntrega(idEntrega) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_ENTREGAS_API + "/pcm/downloadPCM/" + idEntrega,
      { responseType: "blob" },
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function downloadRecibo(idEntrega) {
  try {
    const relatorios = await consultarRelatorios(idEntrega);
    const recibo = relatorios.find(
      (x) => x.IdRelatorioNavigation.NomeRelatorio === "ReciboPCM",
    );

    if (!recibo) {
      throw new PortalError("Relatório não encontrado");
    }
    const response = await Service.$http.get(
      process.env.VUE_APP_REPORTS_API +
      "/relatoriopcm/relatorioPcm?idPCM=" +
      idEntrega +
      "&idRelPcm=" +
      recibo.IdRelatorioPcm,
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function cancelarPcm(idPcm: number): Promise<boolean> {
  const response = await Service.$http.patch(
    process.env.VUE_APP_ENTREGAS_API + "/pcm/cancelarPcm/" + idPcm,
  );
  if (response && response.status === 200) {
    return true;
  }
  throw new PortalError(
    response.data.msg ?? "Erro ao cancelar prestação de contas",
  );
}

async function simularAssinatura(id: number): Promise<boolean> {
  const data = new FormData();
  data.append("idPCM", String(id));

  const response = await Service.$http.post(
    process.env.VUE_APP_ENTREGAS_API + "/PCM/processarPcmHomologacao",
    data,
  );
  if (response && response.status === 200) {
    return true;
  }

  throw new PortalError("Erro ao simular assinatura.");
}

async function liberarReenvio(id: number): Promise<boolean> {
  const data = new FormData();
  data.append("idPCM", String(id));
  const response = await Service.$http.post(
    process.env.VUE_APP_ENTREGAS_API + "/PCM/liberarReenvio",
    data,
  );
  if (response && response.status === 200) {
    return true;
  }
  throw new PortalError("Erro ao liberar reenvio.");
}

async function listarContasCaixa(): Promise<ContaCaixa[]> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/PCM/listarContasCaixa",
  );
  if (response && response.status === 200)
    return response.data.map((c: any) => new ContaCaixa(c));
  throw new PortalError("Erro ao listar contas.");
}

async function atualizarContaCaixa(data: ContaCaixaResumida): Promise<any> {
  try {
    const response = await Service.$http.patch(
      process.env.VUE_APP_ENTREGAS_API + "/PCM/atualizarContaCaixa",
      data,
    );
    if (response && response.status >= 200 && response.status < 300)
      return response.data;
    throw new PortalError(
      response.data?.erros || "Não foi possível atualizar conta",
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors,
      );
    } else {
      throw e;
    }
  }
}

async function carregarFilaPcm(ano: number, mesInicial: number, mesFinal: number): Promise<RetornoFilaPCM> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API +
    `/pcm/processamento/${ano}/${mesInicial}/${mesFinal}`,
  );
  //console.log("pcmService.carregarFilaPcm recebeu", response);
  if (response && response.status === 200) {
    return new RetornoFilaPCM(ano, mesInicial, mesFinal, response.data);
  }
  console.error("Erro ao carregar fila de PCM:", ano, mesInicial, mesFinal, response);
  throw new PortalError("Falha ao carregar fila de PCM")
}

async function getContasInativar(): Promise<ContasInativar[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_ENTREGAS_API}/pcm/contasInativar`
    )
    return response.data.map((c: any) => new ContasInativar(c));
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors,
      );
    } else {
      throw e;
    }
  }
}

async function patchContasInativar(data: ContasInativar): Promise<void> {
  try {
    const response = await Service.$http.patch(
      `${process.env.VUE_APP_ENTREGAS_API}/pcm/flInativoContaBancaria`,
      data
    )
    if (response && response.status !== 200)
      throw new PortalError("Falha ao atualizar status da conta bancária")
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors,
      );
    } else {
      throw e;
    }
  }
}

export default pcmService;
